import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, finalize, of, switchMap, tap } from 'rxjs';
import { DateTime } from 'luxon';

import {
  DateRange,
  encodeApiError,
  TApiErrorResponse,
  ViewMode,
} from '@sandbox-platform/shared-ui';

import { CommissionInvoiceApiService } from '../../../../services/commission-invoice-api.service';
import {
  CommissionInvoiceDTO,
  CommissionInvoiceListFilters,
  cloneInvoiceListFilters,
  defaultInvoiceListFilters,
} from '../../../../types/commission-invoice.types';
import { encodeInvoiceSearchParams } from './definitions';

interface CommissionInvoiceListState {
  list: CommissionInvoiceDTO[];
  total: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  error: string | TApiErrorResponse | null;
  currentView: ViewMode;
  selectedDateRange: DateRange;
  filter: CommissionInvoiceListFilters;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

const initialState: CommissionInvoiceListState = {
  list: [],
  total: 0,
  currentPage: 1,
  pageSize: 20,
  loading: false,
  error: null,
  currentView: 'table',
  selectedDateRange: {
    from: DateTime.now().minus({ months: 3 }).startOf('day').toJSDate(),
    to: DateTime.now().endOf('day').toJSDate(),
  },
  filter: defaultInvoiceListFilters(),
  sortBy: 'created_at',
  sortDirection: 'desc',
};

export const CommissionInvoiceListStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasNextPage: computed(
      () => store.currentPage() < Math.ceil(store.total() / store.pageSize())
    ),
    invoices: computed(() => store.list()),
    isLoading: computed(() => store.loading()),
  })),
  withMethods((store, apiService = inject(CommissionInvoiceApiService)) => {
    const buildSearchParams = (page: number) =>
      encodeInvoiceSearchParams(
        page,
        store.pageSize(),
        store.selectedDateRange(),
        store.filter(),
        store.sortBy(),
        store.sortDirection()
      );

    return {
      get: rxMethod<void>(
        switchMap(() => {
          patchState(store, { loading: true, error: null });

          return apiService.list(buildSearchParams(1)).pipe(
            tap((response) => {
              if (response?.data) {
                patchState(store, {
                  list: response.data.items ?? [],
                  total: response.data.total,
                  currentPage: response.data.page,
                  pageSize: response.data.limit,
                });
              }
            }),
            catchError((error) => {
              patchState(store, {
                error: encodeApiError(error, 'Failed to load invoices'),
              });
              return of(null);
            }),
            finalize(() => patchState(store, { loading: false }))
          );
        })
      ),

      loadMore: rxMethod<void>(
        switchMap(() => {
          if (store.loading() || !store.hasNextPage()) {
            return of(null);
          }

          patchState(store, { loading: true, error: null });
          const nextPage = store.currentPage() + 1;

          return apiService.list(buildSearchParams(nextPage)).pipe(
            tap((response) => {
              if (response?.data) {
                patchState(store, {
                  list: [...store.list(), ...(response.data.items ?? [])],
                  total: response.data.total,
                  currentPage: response.data.page,
                  pageSize: response.data.limit,
                });
              }
            }),
            catchError((error) => {
              patchState(store, {
                error: encodeApiError(error, 'Failed to load invoices'),
              });
              return of(null);
            }),
            finalize(() => patchState(store, { loading: false }))
          );
        })
      ),

      setCurrentView(currentView: ViewMode): void {
        patchState(store, { currentView });
      },

      setDateRange(from: Date | null, to: Date | null): void {
        patchState(store, {
          selectedDateRange: { from, to },
          currentPage: 1,
        });
        this.get();
      },

      onFromDateChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        const fromDate = target.value ? new Date(target.value) : null;
        const currentRange = store.selectedDateRange();
        this.setDateRange(fromDate, currentRange.to);
      },

      onToDateChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        const toDate = target.value ? new Date(target.value) : null;
        const currentRange = store.selectedDateRange();
        this.setDateRange(currentRange.from, toDate);
      },

      setFilters(filters: CommissionInvoiceListFilters): void {
        patchState(store, {
          filter: cloneInvoiceListFilters(filters),
          currentPage: 1,
        });
        this.get();
      },

      setSort(sortBy: string, sortDirection: 'asc' | 'desc'): void {
        patchState(store, { sortBy, sortDirection, currentPage: 1 });
        this.get();
      },

      clearError(): void {
        patchState(store, { error: null });
      },
    };
  })
);

export type CommissionInvoiceListStoreType = InstanceType<
  typeof CommissionInvoiceListStore
>;
